import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../store';
import { usePermissions } from '../hooks/usePermissions';
import {
  FolderKanban,
  Clock,
  CheckCircle2,
  AlertCircle,
  GanttChart,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

import { projects as projectsApi, tasks as tasksApi } from '../api';
import { fmt, STATUS } from '../utils';
import CreateProjectModal from '../components/Projects/CreateProjectModal';

// ── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    projectsApi.stats(project._id)
      .then(setStats)
      .catch(() => { });
  }, [project._id]);

  return (
    <Link
      to={`/project/${project._id}`}
      className="card p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 block group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: (project.color || '#4f63ff') + '20' }}
        >
          {project.icon || '📋'}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate group-hover:text-brand-600 transition-colors">
            {project.name}
          </h3>

          <p className="text-xs text-gray-400 mt-0.5">
            {project.key} · {project.members?.length || 1} участн.
          </p>
        </div>
      </div>

      {project.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
          {project.description}
        </p>
      )}

      {stats && (
        <div className="flex items-center gap-2 sm:gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3 flex-wrap">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            {stats.byStatus?.done || 0}
          </span>

          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-amber-500" />
            {stats.byStatus?.in_progress || 0}
          </span>

          {stats.overdue > 0 && (
            <span className="flex items-center gap-1 text-red-500">
              <AlertCircle className="w-3.5 h-3.5" />
              {stats.overdue}
            </span>
          )}
        </div>
      )}

      {stats && (
        <div>
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Прогресс</span>
            <span>{stats.completionRate}%</span>
          </div>

          <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${stats.completionRate}%`,
                background: project.color || '#4f63ff'
              }}
            />
          </div>
        </div>
      )}
    </Link>
  );
}

// ── Gantt ────────────────────────────────────────────────────────────────────

const ROW_H = 38;
const LABEL_W_GANTT = 240;

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function diffDays(a, b) {
  return Math.round(
    (startOfDay(b) - startOfDay(a)) / 86400000
  );
}

function isWknd(d) {
  return d.getDay() === 0 || d.getDay() === 6;
}

function MonthHeader({ start, days, dayW }) {
  const months = [];
  let cur = new Date(start);

  while (diffDays(start, cur) < days) {
    const month = cur.getMonth();
    const year = cur.getFullYear();

    let count = 0;
    const ms = new Date(cur);

    while (
      diffDays(start, cur) < days &&
      cur.getMonth() === month
    ) {
      count++;
      cur = addDays(cur, 1);
    }

    months.push({
      label: ms.toLocaleDateString('ru', {
        month: 'long',
        year: 'numeric'
      }),
      days: count,
      key: `${year}-${month}`
    });
  }

  return (
    <div
      className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#192230]"
      style={{ height: ROW_H }}
    >
      {months.map(m => (
        <div
          key={m.key}
          className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 px-2 border-r border-gray-200 dark:border-gray-700 truncate capitalize flex items-center"
          style={{ width: m.days * dayW }}
        >
          {m.label}
        </div>
      ))}
    </div>
  );
}

function DashGantt({ projects, allTasks }) {
  const { openTask } = useStore();

  const [dayW, setDayW] = useState(40);
  const [offset, setOffset] = useState(0);

  const containerRef = useRef();

  const labelsScrollRef = useRef();
  const chartScrollRef = useRef();

  const tasksForGantt = useMemo(() => {
    return allTasks
      .filter(t => !t.parent)
      .filter(t => t.dueDate);
  }, [allTasks]);

  const { rangeStart, days } = useMemo(() => {
    const today = startOfDay(new Date());

    if (!tasksForGantt.length) {
      return {
        rangeStart: addDays(today, -30),
        days: 120
      };
    }

    const dates = tasksForGantt.flatMap(t =>
      [
        t.createdAt && new Date(t.createdAt),
        t.dueDate && new Date(t.dueDate)
      ].filter(Boolean)
    );

    const minD = startOfDay(
      new Date(Math.min(...dates))
    );

    const maxD = startOfDay(
      new Date(Math.max(...dates))
    );

    const span = diffDays(minD, maxD) + 1;

    return {
      rangeStart: addDays(minD, -180 + offset),
      days: span + 360
    };
  }, [tasksForGantt, offset]);

  const today = startOfDay(new Date());
  const todayOff = diffDays(rangeStart, today);

  // scroll to today
  useEffect(() => {
    if (!chartScrollRef.current) return;
    if (todayOff < 0) return;

    chartScrollRef.current.scrollLeft =
      Math.max(0, todayOff * dayW);
  }, [todayOff, dayW, rangeStart]);

  // sync vertical scroll
  useEffect(() => {
    const left = labelsScrollRef.current;
    const right = chartScrollRef.current;

    if (!left || !right) return;

    let syncing = false;

    const syncLeft = () => {
      if (syncing) return;

      syncing = true;
      right.scrollTop = left.scrollTop;

      requestAnimationFrame(() => {
        syncing = false;
      });
    };

    const syncRight = () => {
      if (syncing) return;

      syncing = true;
      left.scrollTop = right.scrollTop;

      requestAnimationFrame(() => {
        syncing = false;
      });
    };

    left.addEventListener('scroll', syncLeft);
    right.addEventListener('scroll', syncRight);

    return () => {
      left.removeEventListener('scroll', syncLeft);
      right.removeEventListener('scroll', syncRight);
    };
  }, []);

  const grouped = useMemo(() => {
    return projects
      .map(p => ({
        project: p,
        tasks: tasksForGantt.filter(
          t =>
            t.project === p._id ||
            t.project?._id === p._id
        )
      }))
      .filter(g => g.tasks.length > 0);
  }, [projects, tasksForGantt]);

  const rows = useMemo(() => {
    const r = [];

    grouped.forEach(g => {
      r.push({
        type: 'project',
        project: g.project
      });

      g.tasks.forEach(t =>
        r.push({
          type: 'task',
          task: t,
          project: g.project
        })
      );
    });

    return r;
  }, [grouped]);

  const totalW = days * dayW;

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm py-10">
        <div className="text-center">
          <GanttChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>
            Добавьте задачи с датами для
            отображения диаграммы
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <button
          onClick={() => setOffset(0)}
          className="btn-secondary py-1 px-2 text-xs"
        >
          <Calendar className="w-3.5 h-3.5" />
          Сегодня
        </button>

        <button
          onClick={() => setOffset(o => o - 14)}
          className="btn-ghost p-1.5"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <button
          onClick={() => setOffset(o => o + 14)}
          className="btn-ghost p-1.5"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        <div className="ml-auto flex gap-1">
          <button
            onClick={() =>
              setDayW(w => Math.max(12, w - 6))
            }
            className="btn-ghost p-1.5"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <button
            onClick={() =>
              setDayW(w => Math.min(80, w + 6))
            }
            className="btn-ghost p-1.5"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Gantt */}
      <div
        ref={containerRef}
        className="flex flex-1 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden min-h-0"
      >
        {/* LEFT */}
        <div
          ref={labelsScrollRef}
          className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto overflow-x-hidden hidd"
          style={{
            width: LABEL_W_GANTT,
            minWidth: 120
          }}
        >
          {/* spacer */}
          <div
            style={{ height: ROW_H }}
            className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#192230] sticky top-0 z-20"
          />

          {/* title row */}
          <div
            style={{ height: ROW_H }}
            className="border-b border-gray-200 dark:border-gray-700 flex items-center px-3 bg-gray-50 dark:bg-[#192230] sticky top-[38px] z-20"
          >
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Задача
            </span>
          </div>

          {/* rows */}
          {rows.map((row, i) =>
            row.type === 'project' ? (
              <div
                key={i}
                style={{ height: ROW_H }}
                className="flex items-center px-3 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700"
              >
                <span className="mr-1.5">
                  {row.project.icon || '📋'}
                </span>

                <span className="text-xs font-semibold truncate">
                  {row.project.name}
                </span>
              </div>
            ) : (
              <div
                key={i}
                style={{ height: ROW_H }}
                onClick={() =>
                  openTask(row.task._id)
                }
                className="flex items-center px-3 pl-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 border-b border-gray-50 dark:border-gray-800/50 transition-colors"
              >
                <span className="text-xs truncate hover:text-brand-600 dark:hover:text-brand-400">
                  {row.task.title}
                </span>
              </div>
            )
          )}
        </div>

        {/* RIGHT */}
        <div
          ref={chartScrollRef}
          className="overflow-auto relative flex-1 min-w-0"
        >
          <div
            style={{
              width: totalW,
              minWidth: totalW,
              position: 'relative'
            }}
          >
            {/* Month header */}
            <div className="sticky top-0 z-30">
              <MonthHeader
                start={rangeStart}
                days={days}
                dayW={dayW}
              />
            </div>

            {/* Days header */}
            <div
              className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#192230] sticky top-[38px] z-30"
              style={{ height: ROW_H }}
            >
              {Array.from(
                { length: days },
                (_, i) => {
                  const d = addDays(
                    rangeStart,
                    i
                  );

                  const isToday =
                    diffDays(today, d) === 0;

                  const wknd = isWknd(d);

                  return (
                    <div
                      key={i}
                      style={{
                        width: dayW,
                        minWidth: dayW
                      }}
                      className={`
                        text-center
                        text-[10px]
                        flex
                        items-center
                        justify-center
                        border-r
                        border-gray-100
                        dark:border-gray-800
                        flex-shrink-0
                        ${
                          isToday
                            ? 'font-bold text-brand-600'
                            : wknd
                              ? 'text-gray-300 dark:text-gray-600'
                              : 'text-gray-400'
                        }
                      `}
                    >
                      {dayW >= 20
                        ? d.getDate()
                        : d.getDate() % 5 === 0
                          ? d.getDate()
                          : ''}
                    </div>
                  );
                }
              )}
            </div>

            {/* Rows */}
            <div style={{ position: 'relative' }}>
              {rows.map((row, i) => (
                <div
                  key={i}
                  style={{
                    height: ROW_H,
                    position: 'relative'
                  }}
                  className={`
                    border-b
                    ${
                      row.type === 'project'
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30'
                        : 'border-gray-50 dark:border-gray-800/40'
                    }
                  `}
                >
                  {/* day cols */}
                  {Array.from(
                    { length: days },
                    (_, j) => {
                      const d = addDays(
                        rangeStart,
                        j
                      );

                      return (
                        <div
                          key={j}
                          style={{
                            position:
                              'absolute',
                            left: j * dayW,
                            width: dayW,
                            top: 0,
                            bottom: 0
                          }}
                          className={`
                            border-r
                            border-gray-50
                            dark:border-gray-800/40
                            ${
                              isWknd(d)
                                ? 'bg-gray-50/50 dark:bg-gray-800/20'
                                : ''
                            }
                          `}
                        />
                      );
                    }
                  )}

                  {/* today line */}
                  {todayOff >= 0 &&
                    todayOff < days && (
                      <div
                        style={{
                          position:
                            'absolute',
                          left:
                            todayOff *
                              dayW +
                            dayW / 2,
                          top: 0,
                          bottom: 0,
                          width: 1
                        }}
                        className="bg-brand-400/60 pointer-events-none z-10"
                      />
                    )}

                  {/* task bar */}
                  {row.type === 'task' &&
                    (() => {
                      const t = row.task;

                      if (!t.dueDate)
                        return null;

                      const startDate =
                        t.createdAt
                          ? startOfDay(
                              new Date(
                                t.createdAt
                              )
                            )
                          : startOfDay(
                              new Date(
                                t.dueDate
                              )
                            );

                      const endDate =
                        startOfDay(
                          new Date(
                            t.dueDate
                          )
                        );

                      const bStart =
                        diffDays(
                          rangeStart,
                          startDate
                        );

                      const bEnd =
                        diffDays(
                          rangeStart,
                          endDate
                        );

                      const bDays =
                        Math.max(
                          bEnd -
                            bStart +
                            1,
                          1
                        );

                      const s =
                        STATUS[t.status];

                      const overdue =
                        t.dueDate &&
                        new Date(
                          t.dueDate
                        ) < today &&
                        t.status !==
                          'done' &&
                        t.status !==
                          'review';

                      const color =
                        t.status ===
                        'done'
                          ? '#34d399'
                          : overdue
                            ? '#ef4444'
                            : row.project
                                .color ||
                              s?.color ||
                              '#4f63ff';

                      return (
                        <div
                          onClick={() =>
                            openTask(
                              t._id
                            )
                          }
                          className="absolute rounded cursor-pointer hover:brightness-90 transition-all flex items-center"
                          style={{
                            left:
                              bStart *
                                dayW +
                              1,
                            width:
                              Math.max(
                                bDays *
                                  dayW -
                                  2,
                                6
                              ),
                            top: 5,
                            height:
                              ROW_H -
                              10,
                            background:
                              color,
                            opacity:
                              t.status ===
                              'done'
                                ? 0.6
                                : 1
                          }}
                        >
                          {bDays *
                            dayW >
                            50 && (
                            <span className="px-1.5 text-white text-[10px] truncate select-none">
                              {
                                t.title
                              }
                            </span>
                          )}
                        </div>
                      );
                    })()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { projects, user } = useStore();

  const { canCreateProject } =
    usePermissions();

  const [showCreate, setShowCreate] =
    useState(false);

  const [tab, setTab] =
    useState('projects');

  const [allTasks, setAllTasks] =
    useState([]);

  const hour = new Date().getHours();

  const greeting =
    hour < 12
      ? 'Доброе утро'
      : hour < 18
        ? 'Добрый день'
        : 'Добрый вечер';

  useEffect(() => {
    if (
      tab === 'gantt' &&
      projects.length > 0
    ) {
      Promise.all(
        projects.map(p =>
          tasksApi.list({
            project: p._id
          })
        )
      )
        .then(results => {
          setAllTasks(results.flat());
        })
        .catch(() => { });
    }
  }, [tab, projects]);

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-6xl mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-5 sm:mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {greeting},{' '}
              {
                user?.name?.split(
                  ' '
                )[0]
              }{' '}
              👋
            </h1>

            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">
              {fmt.date(new Date())} ·{' '}
              {projects.length}{' '}
              проектов
            </p>
          </div>

          {canCreateProject() ? (
            <button
              onClick={() =>
                setShowCreate(true)
              }
              className="btn-primary py-1.5"
            >
              <FolderKanban className="w-4 h-4" />
              <span>
                Новый проект
              </span>
            </button>
          ) : (
            <button
              disabled
              title="Требуется авторизация"
              className="btn-primary py-1.5 opacity-50 cursor-not-allowed"
            >
              <FolderKanban className="w-4 h-4" />
              <span>
                Новый проект
              </span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 w-fit mb-5">
          <button
            onClick={() =>
              setTab('projects')
            }
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
              ${
                tab === 'projects'
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }
            `}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Проекты
          </button>

          <button
            onClick={() =>
              setTab('gantt')
            }
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
              ${
                tab === 'gantt'
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }
            `}
          >
            <GanttChart className="w-3.5 h-3.5" />
            Общий Гант
          </button>
        </div>

        {/* Content */}
        {tab === 'projects' &&
          (projects.length === 0 ? (
            <div className="card p-10 sm:p-16 text-center">
              <FolderKanban className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />

              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Пока нет проектов
              </p>

              {canCreateProject() ? (
                <button
                  onClick={() =>
                    setShowCreate(
                      true
                    )
                  }
                  className="btn-primary mx-auto"
                >
                  <FolderKanban className="w-4 h-4" />
                  Создать первый
                  проект
                </button>
              ) : (
                <button
                  disabled
                  title="Требуется авторизация"
                  className="btn-primary mx-auto opacity-50 cursor-not-allowed"
                >
                  <FolderKanban className="w-4 h-4" />
                  Создать первый
                  проект
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {projects.map(p => (
                <ProjectCard
                  key={p._id}
                  project={p}
                />
              ))}
            </div>
          ))}

        {tab === 'gantt' && (
          <div className="flex-1 flex flex-col min-h-[400px]">
            <DashGantt
              projects={projects}
              allTasks={allTasks}
            />
          </div>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal
          onClose={() =>
            setShowCreate(false)
          }
        />
      )}
    </div>
  );
}